import { Suspense } from "react";

import { defineMessages } from "react-intl";
import { JiraSoftwarePricingPageProps } from "src/pages/software/jira/pricing/jira-software-pricing-page";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag.ts";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest.ts";

// eslint-disable-next-line complexity
export const JiraPriceFinder = (props: JiraSoftwarePricingPageProps) => {
    const content = {
        free: usePricingPlanComparison("Free"),
        standard: usePricingPlanComparison("Standard"),
        premium: usePricingPlanComparison("Premium"),
        enterprise: usePricingPlanComparison("Enterprise")
    };

    const freePlanFeatureMessages = createPlanFeatureMessages(content, "free");
    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );
    const enterprisePlanFeatureMessages = createPlanFeatureMessages(
        content,
        "enterprise"
    );

    const signupLimit = 100_000;

    const addSignupSourceToUrlsOnPricingPageEnabled = checkFeatureFlagGate(
        featureFlags.addSignupSourceToUrlsOnPricingPage
    );
    const signupUrlSourceQueryParam = addSignupSourceToUrlsOnPricingPageEnabled
        ? "&signupSource=pricing"
        : "";
    // planNames should not be translated
    const jswProductPlans: ProductPlan[] = [
        {
            planName: content.free?.planTitleExternal ?? "Free",
            offeringSku: "jira-software-free",
            description: content.free?.planOneLiner ?? "",
            recommendationLimit: 0,
            signupLimit,
            signupURLSuffix: `?bundle=jira-software&edition=free&editionIntent=free${signupUrlSourceQueryParam}`,
            planFeatureMessages: freePlanFeatureMessages
        },
        {
            planName: content.standard?.planTitleExternal ?? "Standard",
            offeringSku: "jira-software-standard",
            description: content.standard?.planOneLiner ?? "",
            recommendationLimit: 10,
            signupLimit,
            signupURLSuffix: `?bundle=jira-software&edition=standard${signupUrlSourceQueryParam}`,
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: content.premium?.planTitleExternal ?? "Premium",
            offeringSku: "jira-software-premium",
            description: content.premium?.planOneLiner ?? "",
            recommendationLimit: 250,
            signupLimit,
            signupURLSuffix: `?bundle=jira-software&edition=premium${signupUrlSourceQueryParam}`,
            planFeatureMessages: premiumPlanFeatureMessages
        },
        {
            planName: content.enterprise?.planTitleExternal ?? "Enterprise",
            offeringSku: "jira-software-enterprise-sku",
            recommendationLimit: 5000,
            description: content.enterprise?.planOneLiner ?? "",
            signupLimit,
            planFeatureMessages: enterprisePlanFeatureMessages
        }
    ];

    const messages: PriceFinderMessages = defineMessages({
        headerText: {
            id: "JiraSoftwarePricingPageV2.priceFinder.header",
            defaultMessage:
                "<blue>Simple, transparent pricing</blue> for every team."
        },
        unitCountInputText: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputText",
            defaultMessage: "How many users are on your team?"
        },
        unitCountInputTextRedesign: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputDirectBuy: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputDirectBuy",
            defaultMessage: "What's your team size?"
        },
        unitCountInputTerm: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        billingCycleText: {
            id: "JiraSoftwarePricingPage.priceFinder.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        unitCountInputTooltip: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTooltip",
            defaultMessage:
                "A Jira Software user is any user who can log in to Jira Software, and who exists in User Management"
        },
        cardOverSignupLimit: {
            id: "JiraSoftwarePricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "JiraSoftwarePricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "JiraSoftwarePricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "JiraSoftwarePricingPageV2.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        },
        cardFreeDescription: {
            id: "JiraSoftwarePricingPage.priceFinder.cardFreeDescription",
            defaultMessage: "Always free for 10 users"
        }
    });

    return (
        <Suspense>
            <PriceFinderContextProvider
                productPlans={jswProductPlans}
                dataProductKey="jiraSoftware"
                defaultUnitCount={300}
            >
                <PriceFinderSection
                    messages={messages}
                    serverSideCCPPricing={
                        props.pricing?.ccpPricingData?.jiraSoftware
                    }
                    countryCode={props.countryCode}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};

JiraPriceFinder.displayName = "JiraPriceFinder";
